import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  FeedbackContent,
  FeedbackItem,
  listFeedback,
  reviewFeedback,
} from '../../services/feedbackService';

export const FeedbackText: React.FC<{ feedback: FeedbackContent }> = ({ feedback }) => (
  <Box>
    {feedback.strengths.length > 0 && (
      <>
        <Typography fontWeight={600}>What you understood</Typography>
        <ul>
          {feedback.strengths.map((text, i) => (
            <li key={i}>{text}</li>
          ))}
        </ul>
      </>
    )}
    {feedback.areas_to_improve.length > 0 && (
      <>
        <Typography fontWeight={600}>What to work on</Typography>
        <ul>
          {feedback.areas_to_improve.map((text, i) => (
            <li key={i}>{text}</li>
          ))}
        </ul>
      </>
    )}
    <Typography fontWeight={600}>Your next step</Typography>
    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{feedback.next_step}</Typography>
  </Box>
);

export const FeedbackReview: React.FC<{
  item: FeedbackItem;
  onUpdate: (item: FeedbackItem) => void;
  showContext?: boolean;
}> = ({ item, onUpdate, showContext = true }) => {
  const initial = item.edited || item.draft;
  const [strengths, setStrengths] = useState(initial?.strengths.join('\n') || '');
  const [improvements, setImprovements] = useState(initial?.areas_to_improve.join('\n') || '');
  const [nextStep, setNextStep] = useState(initial?.next_step || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const split = (text: string) =>
    text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  const act = async (action: 'save' | 'publish' | 'reject' | 'regenerate') => {
    setBusy(true);
    setError('');
    try {
      onUpdate(
        await reviewFeedback(
          item,
          action,
          action === 'save' || action === 'publish'
            ? {
                strengths: split(strengths),
                areas_to_improve: split(improvements),
                next_step: nextStep.trim(),
                review_flags: initial?.review_flags || [],
              }
            : undefined,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack spacing={2}>
      {showContext && (
        <>
          <Typography variant="subtitle2">
            {item.module_title} · Student {item.student_id}
          </Typography>
          <Typography>
            {item.input?.subject} · Grade {item.input?.grade_level}
          </Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
            <strong>Question:</strong> {item.input?.question}
          </Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
            <strong>Student answer:</strong> {item.input?.student_answer}
          </Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
            <strong>Rubric:</strong> {item.input?.rubric}
          </Typography>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
            <strong>Reference answer:</strong> {item.input?.reference_answer}
          </Typography>
        </>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {item.publication_status === 'published' && item.feedback ? (
        <FeedbackText feedback={item.feedback} />
      ) : (
        <>
          {item.status === 'ready' && (
            <>
              <Alert severity="info">
                Review the draft for accuracy and age-appropriate language before publishing.
              </Alert>
              {initial?.review_flags?.map((flag, i) => (
                <Alert severity="warning" key={i}>
                  {flag}
                </Alert>
              ))}
              <TextField
                label="What the student understood (one point per line)"
                multiline
                minRows={2}
                value={strengths}
                onChange={(e) => setStrengths(e.target.value)}
                disabled={busy}
              />
              <TextField
                label="What to work on (one point per line)"
                multiline
                minRows={2}
                value={improvements}
                onChange={(e) => setImprovements(e.target.value)}
                disabled={busy}
              />
              <TextField
                label="One next step"
                multiline
                minRows={2}
                value={nextStep}
                onChange={(e) => setNextStep(e.target.value)}
                disabled={busy}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button onClick={() => act('save')} disabled={busy || !nextStep.trim()}>
                  Save draft
                </Button>
                <Button
                  variant="contained"
                  onClick={() => act('publish')}
                  disabled={busy || !nextStep.trim() || item.model_id === 'mock-v1'}
                >
                  Publish feedback
                </Button>
                <Button color="warning" onClick={() => act('reject')} disabled={busy}>
                  Reject draft
                </Button>
              </Stack>
            </>
          )}
          {item.status === 'failed' && (
            <Alert severity="warning">Feedback generation failed. You can retry it.</Alert>
          )}
          {(item.status === 'queued' || item.status === 'processing') && (
            <Alert severity="info">AI feedback is being generated. Refresh progress shortly.</Alert>
          )}
          {(item.status === 'ready' || item.status === 'failed') && (
            <Button onClick={() => act('regenerate')} disabled={busy}>
              Generate a new draft
            </Button>
          )}
        </>
      )}
    </Stack>
  );
};

export const FeedbackInbox: React.FC<{ teacher?: boolean }> = ({ teacher = false }) => {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async (nextCursor?: string) => {
    setBusy(true);
    setError('');
    try {
      const result = await listFeedback(nextCursor);
      setItems((previous) => (nextCursor ? [...previous, ...result.items] : result.items));
      setCursor(result.cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load feedback');
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]); // Explicit refresh avoids replacing unsaved teacher edits.
  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {teacher ? 'Review student feedback' : 'My feedback'}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {teacher
          ? 'Review and publish feedback on descriptive answers.'
          : 'Your teacher will publish feedback here after reviewing your answer.'}
      </Typography>
      <Button onClick={() => load()} disabled={busy}>
        Refresh feedback
      </Button>
      {busy && <CircularProgress size={24} />}
      {error && <Alert severity="error">{error}</Alert>}
      {!busy && !items.length && !error && (
        <Alert severity="info">No feedback submissions yet.</Alert>
      )}
      {items.map((item) => (
        <Card
          key={`${item.feedback_id}-${item.revision || item.publication_status}`}
          sx={{ my: 2 }}
        >
          <CardContent>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={
                  item.publication_status === 'published'
                    ? 'Published'
                    : teacher
                    ? `${item.status} · ${item.publication_status.replace('_', ' ')}`
                    : 'Awaiting teacher feedback'
                }
              />
              <Typography variant="caption">
                Submitted {new Date(item.created_at).toLocaleString()}
              </Typography>
            </Stack>
            {!teacher && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6">{item.module_title}</Typography>
                <Typography>{item.question}</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                  <strong>Your answer:</strong> {item.student_answer}
                </Typography>
              </Box>
            )}
            {teacher ? (
              <FeedbackReview
                item={item}
                onUpdate={(updated) =>
                  setItems((current) =>
                    current.map((value) =>
                      value.feedback_id === updated.feedback_id ? updated : value,
                    ),
                  )
                }
              />
            ) : item.feedback ? (
              <FeedbackText feedback={item.feedback} />
            ) : (
              <Typography>
                Your answer has been saved. Check back after your teacher reviews it.
              </Typography>
            )}
          </CardContent>
        </Card>
      ))}
      {cursor && (
        <Button onClick={() => load(cursor || undefined)} disabled={busy}>
          Load more
        </Button>
      )}
    </Box>
  );
};
